// LayoutPreference — Classic ⇄ Modern レイアウト選択の per-user 永続化 (infrastructure 層).
//
// 背景 (docs/_wip/modern-layout.md §9.4 / §9.4.1、consult 2026-06-01 / 2026-06-08):
//   - レイアウトは純粋なクライアント表示の差 (engine / layers / protocol は
//     レイアウト非依存で1本)。サーバに一切影響しない。
//   - 切替粒度は per-user (同じ部屋で Classic / Modern 混在可)。
//   - グローバル既定 = **Modern** (新規獲得 gateway、Phase 10 β の成長狙い)。
//
// このスライス (`feature/modern-layout-shell`) の確定スコープ (§9.4.1 ③):
//   - 「明示トグルのみ localStorage 永続」の最小チェーンだけを実装する:
//       ユーザ設定 (localStorage) > グローバル既定 (= Modern)
//   - 部屋デフォルト・`?layout=` URL ヒント・キャンペーン導線は **後続スライス送り**
//     (本 module には載せない)。URL ヒントが localStorage を焼くか (A 一過性 /
//     B 限定 sticky) の決定もスライス1 では保留。
//
// 設計 (`uiScalePreference.ts` / `chatFontScalePreference.ts` と同流儀):
//   - 文字列保存 (`'classic' | 'modern'`)。parse 失敗 / 不在 / storage 不可は
//     default 扱い、例外を throw しない。
//   - StorageLike 抽象 + sentinel probe で Safari private browsing / Quota を弾く。
//   - store factory は test で storage mock を注入可能。
//
// Tests: layoutPreference.test.ts (storage-injection style)。
//
// `LayoutKind` は元々ここで独自定義していたが、`@oekaki/protocol` の
// `shared/layout.ts` に同じ 2 値 union が export されたため (`room.join.layout` /
// `analytics.layout-switch.layout` の型として使うため追加、
// pen-usage-analytics-2026-08-18.md §9)、web 側は protocol の型を re-export する
// 形に寄せた。2 箇所に同じ union があると片方だけ値が増えたときに静かにズレる
// ため (2026-08-20)。

/** レイアウト種別. 文字列で localStorage に保存される. `@oekaki/protocol` と同一の型. */
export type { LayoutKind } from '@oekaki/protocol';

import type { LayoutKind } from '@oekaki/protocol';

/**
 * デフォルト値 (= Modern). §9.4 「グローバル既定 = Modern」(新規獲得 gateway)。
 * 旧 Java 版懐古ユーザは明示トグル or (後続スライスの) `?layout=classic` で Classic へ。
 */
export const DEFAULT_LAYOUT: LayoutKind = 'modern';

/** localStorage キー: ユーザのレイアウト選択. */
export const LAYOUT_KEY = 'oekaki:layout';

/**
 * 文字列が有効な LayoutKind か判定する type guard. unknown / 不正値の defensive
 * narrowing 用.
 */
export const isLayoutKind = (v: unknown): v is LayoutKind => v === 'classic' || v === 'modern';

/**
 * Minimal Storage shape this module uses (uiScalePreference.ts と同流儀).
 * `globalThis.localStorage` matches; tests pass a mock with the same surface.
 */
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface LayoutPreferenceStore {
  /** 現在のレイアウト選択を読み出し. 不在 / 不正 / storage 不可 → `DEFAULT_LAYOUT`. */
  get(): LayoutKind;
  /** レイアウト選択を保存. storage 不可なら no-op (例外を throw しない). */
  set(layout: LayoutKind): void;
  /** key を削除. テスト / 設定リセット用. storage 不可なら no-op. */
  clear(): void;
}

/**
 * `globalThis.localStorage` をプローブして使用可能なら返す, さもなくば `null`.
 * uiScalePreference.ts の probe を流用 (sentinel write + readback で Safari
 * private browsing / QuotaExceeded を弾く).
 */
const probeBrowserStorage = (): StorageLike | null => {
  try {
    const ls = globalThis.localStorage;
    if (!ls) return null;
    const sentinel = 'oekaki:layout__probe__';
    ls.setItem(sentinel, '1');
    const got = ls.getItem(sentinel);
    ls.removeItem(sentinel);
    if (got !== '1') return null;
    return ls;
  } catch {
    return null;
  }
};

export interface CreateLayoutPreferenceStoreOptions {
  /**
   * Override the underlying storage. Default: probe `globalThis.localStorage`,
   * fall through to no-op if unavailable. Pass `null` to **force** the no-op
   * path (= getter always returns DEFAULT_LAYOUT, setter silently drops).
   *
   * テストでは小さな `Map` ベースの fake を渡す.
   */
  storage?: StorageLike | null;
}

const readLayout = (storage: StorageLike): LayoutKind => {
  try {
    const raw = storage.getItem(LAYOUT_KEY);
    if (raw === null) return DEFAULT_LAYOUT;
    return isLayoutKind(raw) ? raw : DEFAULT_LAYOUT;
  } catch {
    return DEFAULT_LAYOUT;
  }
};

const writeLayout = (storage: StorageLike, layout: LayoutKind): void => {
  try {
    storage.setItem(LAYOUT_KEY, layout);
  } catch {
    // QuotaExceeded / SecurityError 等 — 永続化できないだけで動作は継続させる.
  }
};

/**
 * `LayoutPreferenceStore` のインスタンスを構築. storage が unavailable な環境
 * (SSR / Safari private browsing 厳格モード) でも getter は default を返すだけで、
 * setter は no-op (= 例外で UI が壊れることはない).
 */
export const createLayoutPreferenceStore = (
  opts: CreateLayoutPreferenceStoreOptions = {},
): LayoutPreferenceStore => {
  const explicit = opts.storage;
  const storage = explicit === null ? null : (explicit ?? probeBrowserStorage());

  return {
    get() {
      if (storage === null) return DEFAULT_LAYOUT;
      return readLayout(storage);
    },
    set(layout) {
      if (storage === null) return;
      writeLayout(storage, layout);
    },
    clear() {
      if (storage === null) return;
      try {
        storage.removeItem(LAYOUT_KEY);
      } catch {
        // ignore
      }
    },
  };
};
